import React from 'react';
import { Navbar } from '../../components/Navbar';
import { ContentBox } from '../../components/ContentBox';
import { ContactUsButton } from '../../components/buttons/ContactUsButton';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  ClipboardCheck,
  Wrench,
  FileOutput,
  UserPlus,
  Users,
  Building2,
} from 'lucide-react';

import HomePageBG from '../../assets/HomepageBG.jpg';
import SLIEcosystemDiagram from '../../assets/SLI-ecosystem-diagram.svg';
import { ROUTES } from '../../constants/routes';

// ============================================================
// GRAPHIC PLACEHOLDER FLAGS
// The following assets are currently missing and should be
// generated to replace inline placeholders in future iterations:
//
// 1. The Challenge section (Section 3) includes an inline SVG
//    "Funding Flow" infographic (✅ IMPLEMENTED). Future improvement:
//    a higher-fidelity illustrated version could replace the SVG,
//    e.g. a map of the US showing conservation funding flows,
//    or illustrated icons for each funding type.
//
// 2. The SLI Mission section (Section 2) now displays the
//    `SLI-ecosystem-diagram.svg` (uploaded graphic). ✅ IMPLEMENTED.
//
// 3. The "Partner / Organization" section is planned for a future
//    iteration (likely Section 7) once logos are finalized.
//    GRAPHIC NEEDED: A partner logos bar or organization
//    acknowledgment section.
// ============================================================

const HomePage = () => {
  const { t } = useTranslation();

  return (
    <div>
      {/* ── SECTION 1: HERO ─────────────────────────────────── */}
      <Navbar
        src={HomePageBG}
        pageName={
          <span className="text-[3.0469rem] lg:text-[3.6562rem] xl:text-[4.875rem]">
            {t('homePage.navbar.pageName')}
          </span>
        }
        subtitle={t('homePage.navbar.subtitle')}
        pageText={t('homePage.navbar.pageText')}
      />

      {/* ── SECTION 2: SLI MISSION ───────────────────────────── */}
      <ContentBox
        id="mission"
        title={t('homePage.mission.title')}
        content={
          <div className="flex flex-col gap-8 md:grid md:grid-cols-2 md:gap-12 md:items-start">
            <div className="space-y-4">
              <p className="text-base leading-8">{t('homePage.mission.paragraph1')}</p>
              <p className="text-base leading-8">{t('homePage.mission.paragraph2')}</p>
            </div>

            {/* SLI Ecosystem Diagram */}
            <img
              src={SLIEcosystemDiagram}
              alt="SLI Ecosystem Diagram"
              className="w-[450px] h-auto mx-auto rounded-lg"
            />
          </div>
        }
        bgColor={'#FAF8F8'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />

      {/* ── SECTION 3: THE CHALLENGE ───────────────────────── */}
      <ContentBox
        title={t('homePage.challenge.title')}
        content={
          <div className="space-y-6">
            <p className="text-base leading-8">{t('homePage.challenge.paragraph1')}</p>

            {/* Inline SVG infographic: funding sources → CP (with tool icons) → farmers */}
            <div className="w-full overflow-x-auto">
              <svg
                viewBox="0 0 860 260"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full min-w-[600px] max-w-[860px] mx-auto"
                role="img"
                aria-label="Conservation funding infographic"
              >
                <defs>
                  <marker
                    id="arr-dark"
                    markerWidth="9"
                    markerHeight="7"
                    refX="8"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 9 3.5, 0 7" fill="#1D4D2B" />
                  </marker>
                  <marker
                    id="arr-green"
                    markerWidth="9"
                    markerHeight="7"
                    refX="8"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 9 3.5, 0 7" fill="#2D7A47" />
                  </marker>
                  <marker
                    id="arr-gold"
                    markerWidth="9"
                    markerHeight="7"
                    refX="8"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 9 3.5, 0 7" fill="#C8922A" />
                  </marker>
                  <filter id="shadow" x="-15%" y="-15%" width="130%" height="130%">
                    <feDropShadow dx="1" dy="2" stdDeviation="4" floodOpacity="0.15" />
                  </filter>
                </defs>

                {/* ══ FEDERAL CARD ══ */}
                <rect
                  x="10"
                  y="20"
                  width="140"
                  height="64"
                  rx="10"
                  fill="#1D4D2B"
                  filter="url(#shadow)"
                />
                <text
                  x="80"
                  y="48"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="15"
                  fontWeight="700"
                  fill="#FAF8F4"
                  letterSpacing="1.5"
                >
                  FEDERAL
                </text>
                <text
                  x="80"
                  y="68"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="10"
                  fill="#C8922A"
                >
                  NRCS EQIP &amp; CSP · RCPP
                </text>

                {/* ══ STATE CARD ══ */}
                <rect
                  x="10"
                  y="96"
                  width="140"
                  height="64"
                  rx="10"
                  fill="#2D7A47"
                  filter="url(#shadow)"
                />
                <text
                  x="80"
                  y="124"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="15"
                  fontWeight="700"
                  fill="#FAF8F4"
                  letterSpacing="1.5"
                >
                  STATE
                </text>
                <text
                  x="80"
                  y="144"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#FAF8F4"
                >
                  Grants
                </text>

                {/* ══ PHILANTHROPIC CARD ══ */}
                <rect
                  x="10"
                  y="172"
                  width="140"
                  height="64"
                  rx="10"
                  fill="#C8922A"
                  filter="url(#shadow)"
                />
                <text
                  x="80"
                  y="200"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="13"
                  fontWeight="700"
                  fill="#1D4D2B"
                  letterSpacing="1"
                >
                  PHILANTHROPIC
                </text>
                <text
                  x="80"
                  y="220"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="10"
                  fill="#1D4D2B"
                >
                  Foundations · NGOs
                </text>

                {/* ── ARROWS: Sources → CP bubble ── */}
                <line
                  x1="150"
                  y1="52"
                  x2="228"
                  y2="115"
                  stroke="#1D4D2B"
                  strokeWidth="2.5"
                  markerEnd="url(#arr-dark)"
                />
                <line
                  x1="150"
                  y1="128"
                  x2="228"
                  y2="128"
                  stroke="#2D7A47"
                  strokeWidth="2.5"
                  markerEnd="url(#arr-green)"
                />
                <line
                  x1="150"
                  y1="204"
                  x2="228"
                  y2="141"
                  stroke="#C8922A"
                  strokeWidth="2.5"
                  markerEnd="url(#arr-gold)"
                />

                {/* ── CP LABEL: ABOVE the circle ── */}
                <text
                  x="300"
                  y="36"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#1D4D2B"
                  letterSpacing="1"
                >
                  CONSERVATION PLANNING
                </text>

                {/* ══ CENTER: CP BUBBLE ══ */}
                <circle
                  cx="300"
                  cy="130"
                  r="86"
                  fill="none"
                  stroke="#2D7A47"
                  strokeWidth="1.5"
                  strokeDasharray="6,5"
                  opacity="0.3"
                />
                <circle
                  cx="300"
                  cy="130"
                  r="78"
                  fill="#FAF8F4"
                  stroke="#1D4D2B"
                  strokeWidth="3"
                  filter="url(#shadow)"
                />
                <text
                  x="300"
                  y="87"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#6b7280"
                  fontStyle="italic"
                >
                  current tools
                </text>

                {/* ── Icon row 1: Word + Excel ── */}
                <rect
                  x="246"
                  y="97"
                  width="30"
                  height="36"
                  rx="4"
                  fill="#e8f0fe"
                  stroke="#1D4D2B"
                  strokeWidth="1.5"
                />
                <text
                  x="261"
                  y="111"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="7.5"
                  fontWeight="700"
                  fill="#1D4D2B"
                >
                  .doc
                </text>
                <polygon
                  points="268,133 276,133 276,125"
                  fill="#c5d5f5"
                  stroke="#1D4D2B"
                  strokeWidth="1"
                />
                <text
                  x="261"
                  y="125"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="9"
                >
                  📄
                </text>

                <rect
                  x="302"
                  y="97"
                  width="30"
                  height="36"
                  rx="4"
                  fill="#e6f4ea"
                  stroke="#2D7A47"
                  strokeWidth="1.5"
                />
                <text
                  x="317"
                  y="111"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="7.5"
                  fontWeight="700"
                  fill="#2D7A47"
                >
                  .xls
                </text>
                <line
                  x1="305"
                  y1="120"
                  x2="329"
                  y2="120"
                  stroke="#2D7A47"
                  strokeWidth="1"
                  opacity="0.6"
                />
                <line
                  x1="305"
                  y1="126"
                  x2="329"
                  y2="126"
                  stroke="#2D7A47"
                  strokeWidth="1"
                  opacity="0.6"
                />
                <line
                  x1="317"
                  y1="105"
                  x2="317"
                  y2="133"
                  stroke="#2D7A47"
                  strokeWidth="1"
                  opacity="0.6"
                />

                {/* ── Icon row 2: Map ── */}
                <circle
                  cx="300"
                  cy="165"
                  r="20"
                  fill="#fef9e7"
                  stroke="#C8922A"
                  strokeWidth="1.5"
                />
                <text
                  x="300"
                  y="162"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                >
                  🌍
                </text>
                <text
                  x="300"
                  y="176"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                >
                  📍
                </text>

                <text
                  x="300"
                  y="228"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fill="#6b7280"
                  fontStyle="italic"
                >
                  (the critical link)
                </text>

                {/* ── ARROW: CP → Farmers ── */}
                <line
                  x1="378"
                  y1="130"
                  x2="465"
                  y2="130"
                  stroke="#1D4D2B"
                  strokeWidth="3"
                  markerEnd="url(#arr-dark)"
                />
                <text
                  x="422"
                  y="150"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                  fontWeight="700"
                >
                  plans
                </text>

                {/* ── RIGHT: FARMER BOX ── */}
                <rect
                  x="470"
                  y="38"
                  width="155"
                  height="180"
                  rx="12"
                  fill="#FAF8F4"
                  stroke="#1D4D2B"
                  strokeWidth="3"
                  filter="url(#shadow)"
                />
                <rect x="470" y="38" width="155" height="40" rx="12" fill="#1D4D2B" />
                <rect x="470" y="65" width="155" height="13" fill="#1D4D2B" />
                <text
                  x="547"
                  y="63"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#FAF8F4"
                  letterSpacing="1"
                >
                  FARMERS &amp; LAND
                </text>
                <text
                  x="490"
                  y="100"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                  fontWeight="700"
                >
                  ✓
                </text>
                <text
                  x="503"
                  y="100"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                >
                  {' '}
                  Access funding
                </text>
                <text
                  x="490"
                  y="124"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                  fontWeight="700"
                >
                  ✓
                </text>
                <text
                  x="503"
                  y="124"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                >
                  {' '}
                  Adopt regenerative
                </text>
                <text
                  x="503"
                  y="139"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                >
                  &nbsp;&nbsp;&nbsp;agricultural practices
                </text>
                <text
                  x="490"
                  y="163"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                  fontWeight="700"
                >
                  ✓
                </text>
                <text
                  x="503"
                  y="163"
                  fontFamily="system-ui, sans-serif"
                  fontSize="11"
                  fill="#1D4D2B"
                >
                  {' '}
                  Track outcomes
                </text>
              </svg>
            </div>

            <p className="text-base leading-8">{t('homePage.challenge.paragraph2')}</p>
            <p className="text-base leading-8 font-medium text-SLIGreen">
              {t('homePage.challenge.paragraph3')}
            </p>
          </div>
        }
        bgColor={'#CFE3F6'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />

      {/* ── SECTION 4: farmOS: CP OVERVIEW ─────────────────── */}
      <ContentBox
        title={t('homePage.farmOSCpOverview.title')}
        content={
          <div>
            <p className="text-lg font-medium text-gray-700 mb-2">
              {t('homePage.farmOSCpOverview.subtitle')}
            </p>
            <p className="text-base text-gray-600 mb-10 max-w-2xl">
              {t('homePage.farmOSCpOverview.description')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <UserPlus size={28} className="text-SLIGreen" />,
                  step: '1',
                  ...t('homePage.farmOSCpOverview.steps.step1', { returnObjects: true }),
                },
                {
                  icon: <Building2 size={28} className="text-SLIGreen" />,
                  step: '2',
                  ...t('homePage.farmOSCpOverview.steps.step2', { returnObjects: true }),
                },
                {
                  icon: <MapPin size={28} className="text-SLIGreen" />,
                  step: '3',
                  ...t('homePage.farmOSCpOverview.steps.step3', { returnObjects: true }),
                },
                {
                  icon: <ClipboardCheck size={28} className="text-SLIGreen" />,
                  step: '4',
                  ...t('homePage.farmOSCpOverview.steps.step4', { returnObjects: true }),
                },
                {
                  icon: <Wrench size={28} className="text-SLIGreen" />,
                  step: '5',
                  ...t('homePage.farmOSCpOverview.steps.step5', { returnObjects: true }),
                },
                {
                  icon: <FileOutput size={28} className="text-SLIGreen" />,
                  step: '6',
                  ...t('homePage.farmOSCpOverview.steps.step6', { returnObjects: true }),
                },
              ].map(({ icon, step, title, description }) => (
                <div
                  key={step}
                  className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-SLIGreen1 rounded-lg flex items-center justify-center">
                      {icon}
                    </div>

                    <div>
                      <span className="text-xs font-bold text-SLIGreen uppercase tracking-wide">
                        Step {step}
                      </span>
                      <h3 className="text-base font-semibold text-gray-800 mt-1">{title}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        }
        bgColor={'#FAF8F8'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />

      {/* ── SECTION 4B: farmOS:CP BUILDS CAPACITY ────────────── */}
      <ContentBox
        title={t('homePage.farmOSCpCapacity.title')}
        content={
          <div className="space-y-6">
            <p className="text-base leading-8">{t('homePage.farmOSCpCapacity.paragraph1')}</p>
            <p className="text-base leading-8">{t('homePage.farmOSCpCapacity.paragraph2')}</p>

            <div className="w-full overflow-x-auto">
              <svg
                viewBox="0 0 760 320"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full min-w-[520px] max-w-[680px] mx-auto"
                role="img"
                aria-label="Diagram showing farmOS:CP's central project repository connecting to grant-ready data, impact tracking, and support for small and family farms"
              >
                <defs>
                  <marker
                    id="cap-arr-dark"
                    markerWidth="9"
                    markerHeight="7"
                    refX="8"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 9 3.5, 0 7" fill="#1D4D2B" />
                  </marker>
                  <marker
                    id="cap-arr-green"
                    markerWidth="9"
                    markerHeight="7"
                    refX="8"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 9 3.5, 0 7" fill="#2D7A47" />
                  </marker>
                  <marker
                    id="cap-arr-gold"
                    markerWidth="9"
                    markerHeight="7"
                    refX="8"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 9 3.5, 0 7" fill="#C8922A" />
                  </marker>
                  <filter id="cap-shadow" x="-15%" y="-15%" width="130%" height="130%">
                    <feDropShadow dx="1" dy="2" stdDeviation="4" floodOpacity="0.15" />
                  </filter>
                </defs>

                <line
                  x1="270"
                  y1="90"
                  x2="120"
                  y2="230"
                  stroke="#2D7A47"
                  strokeWidth="2.5"
                  markerEnd="url(#cap-arr-green)"
                />
                <line
                  x1="380"
                  y1="90"
                  x2="380"
                  y2="230"
                  stroke="#1D4D2B"
                  strokeWidth="2.5"
                  markerEnd="url(#cap-arr-dark)"
                />
                <line
                  x1="490"
                  y1="90"
                  x2="640"
                  y2="230"
                  stroke="#C8922A"
                  strokeWidth="2.5"
                  markerEnd="url(#cap-arr-gold)"
                />

                <rect
                  x="220"
                  y="20"
                  width="320"
                  height="70"
                  rx="14"
                  fill="#FAF8F4"
                  stroke="#1D4D2B"
                  strokeWidth="3"
                  filter="url(#cap-shadow)"
                />
                <text
                  x="260"
                  y="63"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="26"
                >
                  🗄️
                </text>
                <text
                  x="425"
                  y="52"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="14"
                  fontWeight="700"
                  fill="#1D4D2B"
                >
                  farmOS:CP
                </text>
                <text
                  x="425"
                  y="70"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="10"
                  fill="#6b7280"
                  fontStyle="italic"
                >
                  project repository
                </text>

                <rect
                  x="20"
                  y="230"
                  width="200"
                  height="70"
                  rx="10"
                  fill="#2D7A47"
                  filter="url(#cap-shadow)"
                />
                <text
                  x="40"
                  y="267"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="22"
                >
                  📊
                </text>
                <text
                  x="132"
                  y="255"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#FAF8F4"
                >
                  Grant-Ready
                </text>
                <text
                  x="132"
                  y="271"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#FAF8F4"
                >
                  Data
                </text>
                <text
                  x="120"
                  y="287"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="9"
                  fill="#FAF8F4"
                  opacity="0.85"
                >
                  local needs, accurately counted
                </text>

                <rect
                  x="280"
                  y="230"
                  width="200"
                  height="70"
                  rx="10"
                  fill="#1D4D2B"
                  filter="url(#cap-shadow)"
                />
                <text
                  x="300"
                  y="267"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="22"
                >
                  📋
                </text>
                <text
                  x="392"
                  y="255"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#FAF8F4"
                >
                  Impact Tracking
                </text>
                <text
                  x="392"
                  y="271"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#FAF8F4"
                >
                  &amp; Continuity
                </text>
                <text
                  x="380"
                  y="287"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="9"
                  fill="#C8922A"
                >
                  enhanced team integration
                </text>

                <rect
                  x="540"
                  y="230"
                  width="200"
                  height="70"
                  rx="10"
                  fill="#C8922A"
                  filter="url(#cap-shadow)"
                />
                <text
                  x="562"
                  y="260"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="14"
                >
                  🏡🏡🏡
                </text>
                <text
                  x="562"
                  y="278"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="14"
                >
                  ➜📁
                </text>
                <text
                  x="660"
                  y="255"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#1D4D2B"
                >
                  Support Small
                </text>
                <text
                  x="660"
                  y="271"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="12"
                  fontWeight="700"
                  fill="#1D4D2B"
                >
                  &amp; Family Farms
                </text>
                <text
                  x="640"
                  y="287"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  fontSize="9"
                  fill="#1D4D2B"
                  opacity="0.75"
                >
                  one aggregate application
                </text>
              </svg>
            </div>
          </div>
        }
        bgColor={'#CFE3F6'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />

      {/* ── SECTION 5: GET INVOLVED ────────────────────────── */}
      <ContentBox
        title={t('homePage.getInvolved.title')}
        content={
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="flex-1">
              <p className="text-base leading-8">{t('homePage.getInvolved.paragraph1')}</p>
            </div>
            <div className="flex-shrink-0">
              <ContactUsButton>{t('homePage.getInvolved.contactButton')}</ContactUsButton>
            </div>
          </div>
        }
        bgColor={'#FAF8F8'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />
    </div>
  );
};

export { HomePage };
